"use client";

import { useApolloClient } from "@apollo/client/react";
import type { DeepPartial } from "@apollo/client/utilities";
import Link from "next/link";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocalStorage } from "usehooks-ts";

import { toast } from "@/components/ui/use-toast";
import type { Post } from "@/graphql/codegen/graphql";
import { PostsOrderBy } from "@/graphql/codegen/graphql";
import { useFocusPreferences } from "@/hooks/use-focus-preferences";
import { LocalStorageKeys } from "@/utils/local-storage-keys";

import { UserContext } from "./user-context";

export type PostType = DeepPartial<Post> & {
  hideEditor?: boolean;
};

// temp
export type TempFilters = {
  hideViewed: boolean;
  hideNSFW: "hide" | "blur" | "show";
  useModeration: boolean;
};

export type FeedSnapshot = {
  pinnedPost: PostType | null;
  pinnedPostAssociationId: string | null;
  rawPosts: PostType[];
  resolved: boolean;
};

export type FeedContextType = {
  posts: Array<PostType>;
  rawPosts: Array<PostType>;
  setPosts: Dispatch<SetStateAction<PostType[]>>;
  adminPinnedPosts: PostType[];
  setAdminPinnedPosts: Dispatch<SetStateAction<PostType[]>>;
  updatePostByHash: (
    postHash: string,
    updatedPost: PostType | ((prev: PostType) => PostType),
  ) => void;
  initializePosts: (initialPosts: PostType[], username?: string) => void;
  postCategoryFilter: Record<PostCategoryFilter, boolean>;
  setPostCategoryFilter: Dispatch<
    SetStateAction<Record<PostCategoryFilter, boolean>>
  >;
  pinnedPost: PostType | null;
  setPinnedPost: Dispatch<SetStateAction<PostType | null>>;
  checkIfPostIsPinned: (postHash: string) => boolean;
  pinnedPostAssociationId: string | null;
  setPinnedPostAssociationId: Dispatch<SetStateAction<string | null>>;
  postOrderBy: PostsOrderBy;
  setPostOrderBy: Dispatch<SetStateAction<PostsOrderBy>>;
  resetFeed: () => void;
  onPostCreated: (newPost: PostType) => void;
  getScrollPosition: (key: string) => number;
  setScrollPosition: (key: string, y: number) => void;
  clearScrollPosition: (key: string) => void;
  getFeedSnapshot: (key: string) => FeedSnapshot | null;
  setFeedSnapshot: (key: string, snapshot: FeedSnapshot) => void;
  clearFeedSnapshot: (key: string) => void;
  addAdminPinnedPost: (post: PostType) => void;
  isAdminPinnedPost: (postHash: string) => boolean;
  removeAdminPinnedPost: (postHash: string) => void;
  allPostsLength: number;
  getPostByPosthash: (postHash: string) => PostType | undefined;

  // temp
  tempFilters: TempFilters;
};

export enum PostCategoryFilter {
  Post = "Post",
  Repost = "Repost",
  QuotedRepost = "QuotedRepost",
  WithReplies = "WithReplies",
  GatedPosts = "GatedPosts",
}

export enum MediaPostCategoryFilter {
  ImageUrls = "ImageUrls",
  VideoUrls = "VideoUrls",
}

export enum FeedType {
  MediaPostCategoryFilter = "MediaPostCategoryFilter",
  PostCategoryFilter = "PostCategoryFilter",
}

export type PendingPostItem = {
  id: string;
  body: string;
};

const DEFAULT_PROFILE_FILTERS = {
  [PostCategoryFilter.Post]: true,
  [PostCategoryFilter.Repost]: true,
  [PostCategoryFilter.QuotedRepost]: true,
  [PostCategoryFilter.GatedPosts]: true,
  [PostCategoryFilter.WithReplies]: false,
};

// Note: FeedProvider is used across the app to provide data to the feed, main reason - we need scroll restoration
export const FeedContext = createContext<FeedContextType>(null!);

// Logic moved to hook, because of user-specific feed we don't need to get data from FeedContext
export const useFeedLogic = ({
  enableAdminPinnedPostFilters = true,
}: { enableAdminPinnedPostFilters?: boolean } = {}) => {
  const { preferences } = useFocusPreferences();
  const tempFilters = preferences.feedPreferences;
  const apolloClient = useApolloClient();
  const [posts, setPosts] = useState<PostType[]>([]);
  const [adminPinnedPosts, setAdminPinnedPosts] = useState<PostType[]>([]);
  const [pinnedPost, setPinnedPost] = useState<PostType | null>(null);
  const [pinnedPostAssociationId, setPinnedPostAssociationId] = useState<
    string | null
  >(null);
  const [postCategoryFilter, setPostCategoryFilter] = useLocalStorage<
    Record<PostCategoryFilter, boolean>
  >(LocalStorageKeys.filtersProfile, DEFAULT_PROFILE_FILTERS);

  const [postOrderBy, setPostOrderBy] = useState<PostsOrderBy>(
    PostsOrderBy.TimestampDesc,
  );
  const scrollPositionsRef = useRef<Record<string, number>>({});
  const feedSnapshotsRef = useRef<Record<string, FeedSnapshot>>({});
  const { isUserBlocked } = useContext(UserContext);

  const updatePostByHash = useCallback(
    (
      postHash: string,
      updatedPost: PostType | ((post: PostType) => PostType),
    ) => {
      setPosts((state) =>
        state.map((post) => {
          if (post?.postHash === postHash) {
            const newPostContent =
              typeof updatedPost === "function"
                ? updatedPost(post)
                : updatedPost;

            if (pinnedPost?.postHash === postHash) {
              setPinnedPost(newPostContent);
            }

            try {
              apolloClient.cache.modify({
                id: apolloClient.cache.identify(newPostContent),
                // @ts-expect-error - This is a workaround for a bug in Apollo Client
                fields: newPostContent,
              });
            } catch {
              /* we don't care if it throws error */
            }

            return newPostContent;
          }

          return post;
        }),
      );
      setAdminPinnedPosts((state) =>
        state.map((post) =>
          post?.postHash === postHash
            ? typeof updatedPost === "function"
              ? updatedPost(post)
              : updatedPost
            : post,
        ),
      );
    },
    [apolloClient, pinnedPost],
  );

  const initializePosts = useCallback((initialPosts: PostType[]) => {
    const posts = initialPosts.map((post) => ({ ...post, pending: false }));

    setPosts(posts);
  }, []);

  const checkIfPostIsPinned = useCallback(
    (postHash: string) => {
      if (!pinnedPost) return false;

      return pinnedPost?.postHash === postHash;
    },
    [pinnedPost],
  );

  const resetFeed = useCallback(() => {
    setPosts([]);
  }, []);

  const getScrollPosition = useCallback((key: string) => {
    return scrollPositionsRef.current[key] ?? 0;
  }, []);

  const setScrollPosition = useCallback((key: string, y: number) => {
    scrollPositionsRef.current[key] = y;
  }, []);

  const clearScrollPosition = useCallback((key: string) => {
    delete scrollPositionsRef.current[key];
  }, []);

  const getFeedSnapshot = useCallback((key: string) => {
    return feedSnapshotsRef.current[key] ?? null;
  }, []);

  const setFeedSnapshot = useCallback((key: string, snapshot: FeedSnapshot) => {
    feedSnapshotsRef.current[key] = snapshot;
  }, []);

  const clearFeedSnapshot = useCallback((key: string) => {
    delete feedSnapshotsRef.current[key];
  }, []);

  const onPostCreated = useCallback((newPost: PostType) => {
    // Note: need only to set posts on main
    setPosts((state) => {
      return [newPost, ...state];
    });

    toast({
      variant: "success",
      title: "Success",
      description: (
        <div>
          Your post was successfully submitted.{" "}
          <Link
            className="underline"
            href={`/post/${newPost.postHash}`}
            prefetch={false}
          >
            View Post
          </Link>
        </div>
      ),
    });
  }, []);

  const addAdminPinnedPost = useCallback(
    (post: PostType) => {
      setAdminPinnedPosts((state) => {
        return [post, ...state];
      });
    },
    [setAdminPinnedPosts],
  );

  const removeAdminPinnedPost = useCallback(
    (postHash: string) => {
      setAdminPinnedPosts((state) => {
        return state.filter((post) => post.postHash !== postHash);
      });
    },
    [setAdminPinnedPosts],
  );

  const isAdminPinnedPost = useCallback(
    (postHash: string) => {
      return adminPinnedPosts.some((post) => post.postHash === postHash);
    },
    [adminPinnedPosts],
  );

  const getPostByPosthash = useCallback(
    (postHash: string) => {
      return posts.find((post) => post.postHash === postHash);
    },
    [posts],
  );

  const filteredPosts: PostType[] = useMemo(() => {
    return posts.filter((post, index, self) => {
      if (
        !post.body &&
        !post.repostedPost &&
        !post?.imageUrls?.length &&
        !post?.videoUrls?.length
      )
        return;

      if (post.poster?.publicKey && isUserBlocked(post.poster?.publicKey)) {
        return false;
      }

      if (
        enableAdminPinnedPostFilters &&
        adminPinnedPosts.some((p) => p.postHash === post.postHash)
      ) {
        return false;
      }

      // filter duplicated by posthash - because of timestamp lessThanOrEqualTo in get-feed-filters.tsx
      if (
        !post?.postHash ||
        index !== self.findIndex((p) => p.postHash === post.postHash)
      ) {
        return false;
      }

      return !checkIfPostIsPinned(post.postHash) && !post.isHidden;
    });
  }, [
    adminPinnedPosts,
    checkIfPostIsPinned,
    enableAdminPinnedPostFilters,
    isUserBlocked,
    posts,
  ]);

  return {
    getPostByPosthash,
    allPostsLength: posts.length,
    postOrderBy,
    setPostOrderBy,
    pinnedPostAssociationId,
    setPinnedPostAssociationId,
    checkIfPostIsPinned,
    onPostCreated,
    pinnedPost,
    setPinnedPost,
    updatePostByHash,
    postCategoryFilter: postCategoryFilter || DEFAULT_PROFILE_FILTERS,
    setPostCategoryFilter,
    rawPosts: posts,
    posts: pinnedPost ? [pinnedPost, ...filteredPosts] : filteredPosts,
    setPosts,
    adminPinnedPosts,
    setAdminPinnedPosts,
    addAdminPinnedPost,
    removeAdminPinnedPost,
    isAdminPinnedPost,
    initializePosts,
    resetFeed,
    getScrollPosition,
    setScrollPosition,
    clearScrollPosition,
    getFeedSnapshot,
    setFeedSnapshot,
    clearFeedSnapshot,

    //temp
    tempFilters,
  };
};

export const FeedProvider = ({ children }: { children: ReactNode }) => {
  const feedLogic = useFeedLogic();

  return (
    <FeedContext.Provider value={feedLogic}>{children}</FeedContext.Provider>
  );
};

export const useFeedContext = () => {
  const context = useContext(FeedContext);
  if (context === undefined) {
    console.warn("FOCUS WARNING: usage outside of FeedProvider!");
  }
  return context;
};

type InternalFeedContextType = FeedContextType & {
  onUserBlock?: (publicKey: string) => Promise<void> | void;
};

const FeedInternalContext = createContext<InternalFeedContextType>(
  undefined as never,
);

// Note: this provider is used to get data inside feed content, feed container wrapper, and feed item
export const FeedProviderInternal = ({
  children,
  onUserBlock,
  ...feedLogic
}: { children: ReactNode } & InternalFeedContextType) => {
  return (
    <FeedInternalContext.Provider value={{ ...feedLogic, onUserBlock }}>
      {children}
    </FeedInternalContext.Provider>
  );
};

export const useFeedInternalContext = () => {
  return useContext(FeedInternalContext);
};
